import { memo } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { formatCurrency } from "@/shared/lib/formatters";
import type { GraduationResponseDTO } from "../types/graduation.types";

interface GraduationDeleteDialogProps {
  open: boolean;
  loading: boolean;
  graduation: GraduationResponseDTO | null;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Dialog de confirmação de exclusão (soft delete) de formatura.
 *
 * Não cancela a NF vinculada — para isso o admin deve usar o
 * `GraduationCancelDialog`.
 *
 * @example
 * <GraduationDeleteDialog
 *   open={dialogMode === 'delete'}
 *   loading={dialogLoading}
 *   graduation={selectedGraduation}
 *   onClose={closeDialog}
 *   onConfirm={handleDeleteConfirm}
 * />
 */
export const GraduationDeleteDialog = memo(function GraduationDeleteDialog({
  open,
  loading,
  graduation,
  onClose,
  onConfirm,
}: GraduationDeleteDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Excluir Formatura</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja excluir esta formatura?
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-destructive/10 rounded-md border border-destructive/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{graduation?.eventName}</p>
                {graduation ? (
                  <p className="text-sm text-muted-foreground">
                    Valor: {formatCurrency(graduation.totalValue)}
                  </p>
                ) : null}
                <p className="text-sm text-muted-foreground mt-2">
                  A formatura sai da listagem mas a nota fiscal vinculada
                  permanece intacta. Use <strong>Cancelar</strong> se você
                  precisa também invalidar a NF.
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Voltar
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? "Excluindo..." : "Excluir Formatura"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
